import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Megaphone, Plus, Loader2 } from "lucide-react";
import "@/mockups/deepcoast.css";
import { Section, money } from "@/mockups/dc";
import { MultiDogCard } from "@/components/campaigns/MultiDogCard";
import { AiBuilder } from "@/components/campaigns/AiBuilder";

export const CAMP_API = `${process.env.REACT_APP_BACKEND_URL}/api/campaigns`;
export const STATUS_BADGE = { draft: ["Draft", "neutral"], active: ["Active", "accent"], done: ["Done", "neutral"] };
export const CHANNEL = { sms: "Text", email: "Email", handoff: "Gingr hand-off" };

const Templates = ({ templates, onPick, busy }) => (
  <Section title="Start from a template" sub="Or pick a ready-made campaign. You can edit the rules, order and message before anything is sent." icon={Plus} testid="campaign-templates">
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 10 }}>
      {templates.map((t) => (
        <button key={t.key} className="card" disabled={!!busy} onClick={() => onPick(t.key)} style={{ textAlign: "left", padding: 14, cursor: "pointer" }} data-testid={`template-${t.key}`}>
          <b style={{ fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}>{busy === t.key && <Loader2 size={13} className="animate-spin" />}{t.name}</b>
          <div className="muted" style={{ fontSize: 12, margin: "4px 0 8px" }}>{t.desc}</div>
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>{t.rule_labels.map((l) => <span key={l} className="chip" style={{ fontSize: 10 }}>{l}</span>)}</div>
        </button>
      ))}
    </div>
    {busy && <div className="muted" style={{ fontSize: 12, marginTop: 10 }} data-testid="campaign-building">Finding matching owners in Gingr. This can take up to a minute…</div>}
  </Section>
);

const List = ({ rows, onOpen }) => (
  <Section title="Campaigns" sub="Saved per location. Bookings count when a messaged owner books within 14 days; results update nightly." icon={Megaphone} testid="campaign-list">
    {!rows.length ? <div className="muted" style={{ fontSize: 13 }} data-testid="campaign-list-empty">No campaigns yet. Start one above.</div> : (
      <div style={{ overflowX: "auto" }}>
        <table className="table" style={{ width: "100%" }}>
          <thead><tr>{["Campaign", "Status", "Channel", "Audience", "Sent", "Booked", "Revenue", "Refreshed"].map((h) => <th key={h} style={{ textAlign: "left" }}>{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} onClick={() => onOpen(c.id)} style={{ cursor: "pointer" }} data-testid={`campaign-row-${c.id}`}>
                <td><b>{c.name}</b><div className="muted" style={{ fontSize: 11 }}>by {c.created_by_name} · {new Date(c.created_at).toLocaleDateString()}</div></td>
                <td><span className={`badge ${STATUS_BADGE[c.status][1]}`}>{STATUS_BADGE[c.status][0]}</span></td>
                <td>{CHANNEL[c.channel]}</td>
                <td className="mono">{c.audience}</td>
                <td className="mono">{c.sent}{c.queued ? <span className="muted"> +{c.queued} queued</span> : null}</td>
                <td className="mono">{c.booked}</td>
                <td className="mono">{money(c.revenue || 0)}</td>
                <td className="muted mono" style={{ fontSize: 12 }}>{c.refreshed_at ? new Date(c.refreshed_at).toLocaleString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </Section>
);

export default function Campaigns() {
  const [rows, setRows] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState(null);
  const [denied, setDenied] = useState(false);
  const [params] = useSearchParams();
  const nav = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(CAMP_API).then((r) => setRows(r.data)).catch((e) => { setRows([]); if (e.response?.status === 403) setDenied(true); else toast.error(e.response?.data?.detail || "Couldn't load campaigns"); });
    axios.get(`${CAMP_API}/templates`).then((r) => setTemplates(r.data)).catch(() => {});
  }, []);
  if (denied) return <div className="dc v3" style={{ background: "transparent" }}><div className="alert" data-testid="campaigns-denied">Campaigns are available to managers and admins only.</div></div>;
  const pick = async (template) => {
    setBusy(template);
    try {
      const { data } = await axios.post(CAMP_API, { template });
      nav(`/campaigns/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't build the campaign");
      setBusy(null);
    }
  };
  const pre = params.get("template");
  return (
    <div className="dc v3" style={{ background: "transparent" }} data-testid="campaigns-page">
      <h1 style={{ fontSize: 32, fontWeight: 800, letterSpacing: "-0.02em", margin: "0 0 6px" }}>Campaigns</h1>
      <p className="muted" style={{ fontSize: 14, margin: "0 0 20px" }}>Fill rooms by messaging the right owners first. Nothing is sent until you review and tap Send.</p>
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        {pre && templates.length > 0 && (
          <div className="alert" style={{ alignItems: "center" }} data-testid="campaign-prefill">
            <span>Ready to build <b>{templates.find((t) => t.key === pre)?.name}</b>?</span>
            <button className="btn btn-primary btn-sm" style={{ marginLeft: "auto" }} disabled={!!busy} onClick={() => pick(pre)} data-testid="campaign-prefill-build">Build it</button>
          </div>
        )}
        <AiBuilder />
        <Templates templates={templates} onPick={pick} busy={busy} />
        {rows === null ? <div className="skeleton" style={{ height: 160, borderRadius: 12 }} /> : <List rows={rows} onOpen={(id) => nav(`/campaigns/${id}`)} />}
        <MultiDogCard testid="campaigns-multi-dog" />
      </div>
    </div>
  );
}
